import type { TagJSONObject } from '@ikegps/core-api';
import { action, computed, observable, makeObservable } from 'mobx';
import { T } from '../i18n';
import { TagMapState } from '../modules/tag.map/tag.map.state';
import { Services } from '../services';
import { MobxState } from '../state/mobx';
import { CurrentState } from '../state/page.state';
import { MAX_TAG_SHOWN, TagSortState } from '../state/tag.sort.state';
import { AppStore } from '../store';
import { TagEditState } from './tag.edit.state';

export const PAGE_SIZE = 22;

export enum TagFetchState {
	UNKNOWN = 'UNKNOWN',
	HAS_TAGS = 'HAS_TAGS',
	NO_TAGS = 'NO_TAGS',
	EMPTY_TAGS = 'EMPTY_TAGS',
}

export class TagState extends MobxState {
	map: TagMapState;
	edit: TagEditState;
	@observable loadMoreCount = MAX_TAG_SHOWN;
	loaded: boolean;

	constructor() {
		super();
		makeObservable(this);

		this.loaded = false;
		this.map = new TagMapState(this);
		this.edit = new TagEditState(T.TAG_NAME);

		this.reaction(
			(_) => Services.Department.current,
			() => CurrentState.clearSelected()
		);
	}

	override destroy() {
		super.destroy();
		this.map.destroy();
	}

	@computed get tags(): TagJSONObject[] {
		const tags = [];
		this.loaded = false;

		for (const tag of AppStore.Tag.all) {
			const tagSummary = AppStore.TagSummary.get(tag.objectId);
			if (tagSummary !== undefined) {
				this.loaded = true;
			}
			if (!tagSummary || tagSummary.revisions.length === 0) {
				continue;
			}

			tags.push(tag);
		}

		if (AppStore.Tag.all.length === 0 && AppStore.Tag.fetched) {
			this.loaded = true;
		}
		return tags;
	}

	@computed get filteredTags(): TagJSONObject[] {
		if (AppStore.Tag.all == null) {
			return [];
		}

		const tags = [];
		const shouldCheckFilter = Services.Search.filterState.hasFilter();

		for (const tag of AppStore.Tag.all) {
			if (shouldCheckFilter) {
				const filtered = Services.Search.includesTag(tag) || CurrentState.Tag.isTagSelected(tag.objectId);
				if (!filtered) {
					continue;
				}
			}

			const tagSummary = AppStore.TagSummary.get(tag.objectId);
			if (tagSummary == null || tagSummary.revisions.length === 0) {
				continue;
			}

			tags.push(tag);
		}

		return TagSortState.sortTags(tags);
	}

	@computed get fetchState(): TagFetchState {
		// Fetching tags and tag summaries
		let tags = this.tags;
		if (!AppStore.Tag.fetched || !this.loaded) {
			return TagFetchState.UNKNOWN;
		}
		if (AppStore.Tag.all.length > 0 && this.tags.length === 0 && tags != null) {
			return TagFetchState.EMPTY_TAGS;
		}

		if (this.tags.length > 0) {
			return TagFetchState.HAS_TAGS;
		}

		return TagFetchState.NO_TAGS;
	}

	@computed get visibleTags(): TagJSONObject[] {
		const tags =
			this.filteredTags.length < this.loadMoreCount
				? this.filteredTags
				: this.filteredTags.slice(0, this.loadMoreCount);

		CurrentState.Tag.setVisibleTagListObjectIDs(tags.map((tag) => tag.objectId));

		return tags;
	}

	@computed get hasMore(): boolean {
		return this.filteredTags.length > this.loadMoreCount;
	}

	@action.bound loadMore() {
		this.loadMoreCount += MAX_TAG_SHOWN;

		Services.Analytics.trackEvent(
			Services.Analytics.EventCategory.BigMap,
			'Tag:LoadMore',
			String(this.loadMoreCount),
			1
		);
	}

	@action expandTag(tag: TagJSONObject) {
		CurrentState.setExpandedTagId(tag.objectId);

		Services.Analytics.trackEvent(Services.Analytics.EventCategory.BigMap, 'Tag:Expand', tag.objectId, 1);
	}

	@action collapseTag(tag: TagJSONObject) {
		CurrentState.setExpandedTagId(null);

		Services.Analytics.trackEvent(Services.Analytics.EventCategory.BigMap, 'Tag:Collapse', tag.objectId, 1);
	}

	@computed get allSelected(): boolean {
		for (const tag of this.filteredTags) {
			const tagSummary = AppStore.TagSummary.get(tag.objectId);
			if (
				tagSummary == null ||
				tagSummary.revisions.length !== CurrentState.Tag.collectionsSelectedSize(tag.objectId)
			) {
				return false;
			}
		}

		return true;
	}

	@action.bound
	async inspectHovered() {
		const { hoverId } = CurrentState;
		const canChange = await this.map.checkChangeCollection(hoverId);
		if (canChange) {
			CurrentState.setInspectedCollection(hoverId);
		}
	}
}
